import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable } from 'rxjs';
import { ResolvedAddress } from '../models/resolved-address.model';
import {
  CrmActivity,
  CrmActivityType,
  CrmLead,
  CrmLeadStatus,
  CrmVenue,
  CrmVenueType,
  CRM_STATUS_LABELS,
} from '../models/crm-lead.model';
import { normalizePhoneForMatch } from '../shared/utils/contact.utils';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import { fromCollectionSnapshot } from '../shared/firestore/live-query';

export type CrmLeadInput = Omit<CrmLead, 'id' | 'createdAt' | 'updatedAt'>;
export type CrmVenueInput = Omit<CrmVenue, 'id' | 'createdAt'>;

export interface CrmMapsImportResult {
  leadId: string;
  venueId: string;
  mergedIntoExisting: boolean;
}

@Injectable({ providedIn: 'root' })
export class CrmLeadService extends FirebaseAwareService {
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(Auth);

  watchLeads(): Observable<CrmLead[]> {
    const q = query(
      collection(this.firestore, 'crm_leads'),
      orderBy('updatedAt', 'desc'),
    );
    return fromCollectionSnapshot<CrmLead>(this.injector, q);
  }

  watchActivities(leadId: string): Observable<CrmActivity[]> {
    const q = query(
      collection(this.firestore, 'crm_leads', leadId, 'activities'),
      orderBy('createdAt', 'desc'),
    );
    return fromCollectionSnapshot<CrmActivity>(this.injector, q);
  }

  watchVenues(leadId: string): Observable<CrmVenue[]> {
    const q = query(
      collection(this.firestore, 'crm_leads', leadId, 'venues'),
      orderBy('createdAt', 'desc'),
    );
    return fromCollectionSnapshot<CrmVenue>(this.injector, q);
  }

  async findLeadByPhone(phone: string): Promise<CrmLead | null> {
    const key = normalizePhoneForMatch(phone);
    if (!key) return null;
    const snap = await this.inCtx(() =>
      getDocs(query(
        collection(this.firestore, 'crm_leads'),
        where('phoneNormalized', '==', key),
      )),
    );
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() } as CrmLead;
  }

  venueFromResolved(resolved: ResolvedAddress, mapsUrl: string): CrmVenueInput {
    return {
      venueName: resolved.officialName?.trim() || resolved.addressLine?.trim() || 'Local sin nombre',
      venueType: 'otro' as CrmVenueType,
      city: resolved.district?.trim() || resolved.province?.trim() || '',
      province: resolved.province?.trim() || undefined,
      mapsUrl: mapsUrl.trim(),
      placeId: resolved.placeId || undefined,
      formattedAddress: resolved.formattedAddress || undefined,
      website: resolved.website || undefined,
      source: 'maps',
    };
  }

  /** Add a venue to a specific lead; re-home if phone belongs to another contact. */
  async addVenueFromMapsToLead(
    leadId: string,
    resolved: ResolvedAddress,
    mapsUrl: string,
  ): Promise<CrmMapsImportResult> {
    const venueInput = this.venueFromResolved(resolved, mapsUrl);
    const phone = resolved.phone?.trim();

    if (phone) {
      const other = await this.findLeadByPhone(phone);
      if (other && other.id !== leadId) {
        const venueId = await this.addVenue(other.id, venueInput, { phone, mergeContact: true });
        await this.addActivity(other.id, 'note', `Local añadido desde Google Maps: ${venueInput.venueName}.`);
        return { leadId: other.id, venueId, mergedIntoExisting: true };
      }
    }

    const venueId = await this.addVenue(leadId, venueInput, {
      phone,
      mergeContact: !!phone,
    });
    await this.addActivity(leadId, 'note', `Local añadido: ${venueInput.venueName}.`);
    return { leadId, venueId, mergedIntoExisting: false };
  }

  /** Import from Maps: merge venue into existing lead by phone, or create new lead+venue. */
  async importVenueFromMaps(
    resolved: ResolvedAddress,
    mapsUrl: string,
  ): Promise<CrmMapsImportResult> {
    const venueInput = this.venueFromResolved(resolved, mapsUrl);
    const phone = resolved.phone?.trim();
    const existing = phone ? await this.findLeadByPhone(phone) : null;

    if (existing) {
      const venueId = await this.addVenue(existing.id, venueInput, {
        phone,
        mergeContact: true,
      });
      await this.addActivity(
        existing.id,
        'note',
        `Local añadido desde Google Maps: ${venueInput.venueName}.`,
      );
      return { leadId: existing.id, venueId, mergedIntoExisting: true };
    }

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);

    const leadInput: CrmLeadInput = {
      status: 'identified',
      priority: 'B',
      venueName: venueInput.venueName,
      venueType: venueInput.venueType,
      city: venueInput.city,
      source: 'maps',
      nextAction: 'Primer contacto',
      nextActionAt: Timestamp.fromDate(tomorrow),
      venueCount: 1,
      ...(venueInput.province ? { province: venueInput.province } : {}),
      ...(venueInput.mapsUrl ? { mapsUrl: venueInput.mapsUrl } : {}),
      ...(phone ? {
        phone,
        phoneNormalized: normalizePhoneForMatch(phone),
      } : {}),
      ...(resolved.website ? { website: resolved.website } : {}),
    };

    const leadId = await this.createLead(leadInput);
    const venueId = await this.addVenue(leadId, venueInput, { phone, mergeContact: false });
    await this.addActivity(leadId, 'note', 'Lead creado desde Google Maps.');
    return { leadId, venueId, mergedIntoExisting: false };
  }

  async createLead(input: CrmLeadInput): Promise<string> {
    const payload = this.withPhoneNormalized(input);
    const now = serverTimestamp();
    const ref = await this.inCtx(() =>
      addDoc(collection(this.firestore, 'crm_leads'), {
        ...payload,
        createdAt: now,
        updatedAt: now,
      }),
    );
    return ref.id;
  }

  async createLeadWithVenue(lead: CrmLeadInput, venue: CrmVenueInput): Promise<string> {
    const leadId = await this.createLead({
      ...lead,
      venueName: venue.venueName,
      venueType: venue.venueType,
      city: venue.city,
      venueCount: 1,
      ...(venue.province ? { province: venue.province } : {}),
      ...(venue.mapsUrl ? { mapsUrl: venue.mapsUrl } : {}),
    });
    await this.addVenue(leadId, venue, { phone: lead.phone, mergeContact: false });
    await this.addActivity(leadId, 'note', 'Lead creado manualmente.');
    return leadId;
  }

  async addVenue(
    leadId: string,
    venue: CrmVenueInput,
    opts?: { phone?: string; mergeContact?: boolean },
  ): Promise<string> {
    const ref = await this.inCtx(() =>
      addDoc(collection(this.firestore, 'crm_leads', leadId, 'venues'), {
        ...venue,
        createdAt: serverTimestamp(),
      }),
    );

    const venuesSnap = await this.inCtx(() =>
      getDocs(query(
        collection(this.firestore, 'crm_leads', leadId, 'venues'),
        orderBy('createdAt', 'desc'),
      )),
    );
    const venues = venuesSnap.docs.map(d => ({ id: d.id, ...d.data() } as CrmVenue));
    const primary = venues[0];

    const leadPatch: Partial<CrmLeadInput> = {
      venueName: primary?.venueName ?? venue.venueName,
      venueType: primary?.venueType ?? venue.venueType,
      city: primary?.city ?? venue.city,
      venueCount: venues.length,
      ...(primary?.province ? { province: primary.province } : {}),
      ...(primary?.mapsUrl ? { mapsUrl: primary.mapsUrl } : {}),
    };

    if (opts?.mergeContact && opts.phone?.trim()) {
      const key = normalizePhoneForMatch(opts.phone);
      leadPatch.phone = opts.phone.trim();
      if (key) leadPatch.phoneNormalized = key;
    }

    await this.updateLead(leadId, leadPatch);
    return ref.id;
  }

  async updateLead(leadId: string, patch: Partial<CrmLeadInput>): Promise<void> {
    await this.inCtx(() =>
      updateDoc(doc(this.firestore, 'crm_leads', leadId), {
        ...this.withPhoneNormalized(patch),
        updatedAt: serverTimestamp(),
      }),
    );
  }

  async changeStatus(
    leadId: string,
    from: CrmLeadStatus,
    to: CrmLeadStatus,
    note?: string,
  ): Promise<void> {
    await this.updateLead(leadId, { status: to });
    const body = note?.trim()
      ? `${CRM_STATUS_LABELS[from]} → ${CRM_STATUS_LABELS[to]}: ${note.trim()}`
      : `${CRM_STATUS_LABELS[from]} → ${CRM_STATUS_LABELS[to]}`;
    await this.addActivity(leadId, 'status_change', body);
  }

  /** Hard-delete lead plus `activities` and `venues` subcollections. */
  async deleteLead(leadId: string): Promise<void> {
    await this.inCtx(async () => {
      await this.deleteSubcollection(leadId, 'activities');
      await this.deleteSubcollection(leadId, 'venues');
      await deleteDoc(doc(this.firestore, 'crm_leads', leadId));
    });
  }

  async addActivity(
    leadId: string,
    type: CrmActivityType,
    body: string,
  ): Promise<void> {
    const uid = this.auth.currentUser?.uid ?? 'admin';
    await this.inCtx(() =>
      addDoc(collection(this.firestore, 'crm_leads', leadId, 'activities'), {
        leadId,
        type,
        body: body.trim(),
        createdAt: serverTimestamp(),
        createdBy: uid,
      }),
    );
    await this.updateLead(leadId, {
      lastContactAt: Timestamp.now(),
    } as Partial<CrmLeadInput>);
  }

  private async deleteSubcollection(leadId: string, sub: 'activities' | 'venues'): Promise<void> {
    const snap = await getDocs(collection(this.firestore, 'crm_leads', leadId, sub));
    await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
  }

  /**
   * Creates a minimal lead from the public landing email form (no auth required).
   * Deterministic doc id — re-submit is treated as already registered (update denied by rules).
   * Avoids a crm_leads query (anonymous cannot read the collection).
   */
  async createLeadFromLanding(email: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();
    const id = `landing_${encodeURIComponent(normalizedEmail).replace(/%/g, '_')}`;
    const now = serverTimestamp();
    try {
      await this.inCtx(() =>
        setDoc(doc(this.firestore, 'crm_leads', id), {
          source: 'landing',
          status: 'identified',
          contactEmail: normalizedEmail,
          venueName: '',
          city: '',
          venueType: 'otro',
          priority: 'C',
          createdAt: now,
          updatedAt: now,
        }),
      );
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      // Existing doc → setDoc is update → rules deny → already on the list.
      if (code === 'permission-denied' || code === 'firestore/permission-denied') return;
      throw err;
    }
  }

  private withPhoneNormalized(patch: Partial<CrmLeadInput>): Partial<CrmLeadInput> {
    if (!patch.phone) return patch;
    const key = normalizePhoneForMatch(patch.phone);
    return key ? { ...patch, phoneNormalized: key } : patch;
  }
}
